import { bytesHash } from "./fonts";
import { effectiveFields, type Context } from "./engine";
import type { Project, Value } from "./schema";
import type { Principal } from "./repository";
import { resolveSourceParameters } from "./dynamic-parameters";
export const userLookupCatalog = [
  [1, "fullName", "Full name"],
  [2, "phoneNumber", "Phone"],
  [3, "faxNumber", "Fax"],
  [4, "cityStateZip", "City, state and ZIP"],
  [5, "title", "Title"],
  [6, "addressLine1", "Address line 1"],
  [7, "addressLine2", "Address line 2"],
  [8, "city", "City"],
  [9, "state", "State code"],
  [10, "zip", "ZIP"],
  [11, "firstName", "First name"],
  [12, "lastName", "Last name"],
  [13, "addressLine3", "Address line 3"],
  [14, "email", "Email"],
  [15, "division", "Division"],
  [16, "custom01", "Custom 1"],
  [17, "custom02", "Custom 2"],
  [18, "custom03", "Custom 3"],
  [19, "website", "Website"],
  [20, "title2", "Alternate title"],
  [21, "phone2", "Alternate phone 2"],
  [22, "phone3", "Alternate phone 3"],
  [23, "photo", "Profile photo"],
  [24, "logo", "Customer logo"],
  [26, "externalId", "External ID"],
  [27, "stateName", "State name"],
].map(([id, key, label]) => ({
  id: id as number,
  key: key as string,
  label: label as string,
}));
export const orderLookupCatalog = [
  {
    id: 28,
    key: "ORDER_DISP_NO",
    label: "Order display number",
    placeholder: "~{ORDER_DISP_NO}~",
  },
  {
    id: 29,
    key: "CUSTOM_IDENTIFIER",
    label: "Custom identifier",
    placeholder: "~{CUSTOM_IDENTIFIER}~",
  },
  {
    id: 30,
    key: "PROOF_NAME",
    label: "Proof name",
    placeholder: "~{PROOF_NAME}~",
  },
];
export function legacyLookupId(key?: string) {
  const named = [...userLookupCatalog, ...orderLookupCatalog].find(
    (item) => item.key === key,
  );
  if (named) return named.id;
  if (!/^lookup_\d+$/.test(key || "")) return null;
  const value = Number(key!.slice(7));
  return Number.isSafeInteger(value) && value >= 0 && value <= 2147483647
    ? value
    : null;
}
export class ContextError extends Error {
  constructor(
    readonly code: string,
    readonly status = 400,
  ) {
    super(code);
  }
}
const own = (object: unknown, key: string): unknown =>
  object && typeof object === "object" && Object.hasOwn(object, key)
    ? (object as Record<string, unknown>)[key]
    : undefined;
/** JSON-only contexts are bounded before serialization and copied without prototype setters. */
export function parseContext(raw: unknown): Context {
  let budget = 1000000,
    nodes = 0;
  const walk = (value: unknown, depth: number): unknown => {
    if (++nodes > 10000 || depth > 8) throw new ContextError("CONTEXT_LIMIT");
    if (
      value === null ||
      typeof value === "boolean" ||
      typeof value === "number"
    ) {
      if (typeof value === "number" && !Number.isFinite(value))
        throw new ContextError("CONTEXT_VALUE_INVALID");
      budget -= String(value).length;
    } else if (typeof value === "string") {
      if (value.length > 32768) throw new ContextError("CONTEXT_VALUE_LIMIT");
      budget -= new TextEncoder().encode(JSON.stringify(value)).length;
    } else if (Array.isArray(value)) {
      budget -= value.length + 2;
      const result = value.map((v) => walk(v, depth + 1));
      if (budget < 0) throw new ContextError("CONTEXT_LIMIT");
      return result;
    } else if (
      value &&
      typeof value === "object" &&
      (Object.getPrototypeOf(value) === Object.prototype ||
        Object.getPrototypeOf(value) === null)
    ) {
      const entries = Object.entries(value);
      budget -= entries.length + 2;
      const result = Object.fromEntries(
        entries.map(([key, v]) => {
          if (key.length > 200) throw new ContextError("CONTEXT_KEY_LIMIT");
          budget -= new TextEncoder().encode(JSON.stringify(key)).length + 1;
          return [key, walk(v, depth + 1)];
        }),
      );
      if (budget < 0) throw new ContextError("CONTEXT_LIMIT");
      return result;
    } else throw new ContextError("CONTEXT_VALUE_INVALID");
    if (budget < 0) throw new ContextError("CONTEXT_LIMIT");
    return value;
  };
  const value = walk(raw === undefined ? {} : raw, 0) as Record<
    string,
    unknown
  >;
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).some(
      (k) =>
        ![
          "user",
          "order",
          "forms",
          "dynamic",
          "now",
          "sourceParameters",
        ].includes(k),
    )
  )
    throw new ContextError("CONTEXT_INVALID");
  const values = (map: unknown) => {
    if (
      !map ||
      typeof map !== "object" ||
      Array.isArray(map) ||
      Object.keys(map).length > 1000
    )
      throw new ContextError("CONTEXT_INVALID");
    for (const v of Object.values(map))
      if (Array.isArray(v) && v.some((item) => typeof item !== "string"))
        throw new ContextError("CONTEXT_VALUE_INVALID");
  };
  for (const name of ["user", "order", "dynamic"])
    if (Object.hasOwn(value, name)) values(value[name]);
  if (Object.hasOwn(value, "forms")) {
    values(value.forms);
    for (const form of Object.values(value.forms as object)) values(form);
  }
  if (Object.hasOwn(value, "sourceParameters")) {
    values(value.sourceParameters);
    for (const group of Object.values(value.sourceParameters as object)) {
      if (group === null) continue;
      values(group);
      if (
        Object.values(group).some(
          (item) =>
            item !== null &&
            !["string", "number", "boolean"].includes(typeof item),
        )
      )
        throw new ContextError("CONTEXT_PARAMETERS_INVALID");
    }
  }
  if (Object.hasOwn(value, "now") && typeof value.now !== "string")
    throw new ContextError("CONTEXT_INVALID");
  return value as Context;
}
export interface ContextSource {
  fieldId: string;
  kind: "user" | "order" | "form" | "dynamic";
  key: string;
  instanceId?: string;
  dynamicMode?: "choices";
  dynamicSourceId?: string;
}
export interface ContextRequest {
  version: 1;
  tenantId: string;
  subjectId: string;
  projectId: string;
  projectRevision: number;
  partId?: string;
  sources: ContextSource[];
  clock: boolean;
  parameters?: import("./schema").SourceParameters;
  forceRefresh?: true;
}
export type HostContextProvider = (
  request: Readonly<ContextRequest>,
  signal: AbortSignal,
) => Promise<unknown>;
export function contextRequest(
  project: Project,
  actor: Pick<Principal, "tenantId" | "subject">,
  partId?: string,
  options: { sourceParameters?: unknown; forceRefresh?: boolean } = {},
): ContextRequest {
  if (actor.tenantId !== project.tenantId)
    throw new ContextError("CONTEXT_SCOPE_DENIED", 403);
  const part = project.parts.find((p) => p.id === partId);
  if (partId && !part) throw new ContextError("CONTEXT_PART_UNKNOWN");
  const fields = effectiveFields(project, partId),
    sources: ContextSource[] = [];
  for (const f of fields) {
    if (
      f.autoGenerated ||
      (part &&
        Object.hasOwn(part.systemValues, f.id) &&
        !(f.source.kind === "dynamic" && f.source.dynamicMode === "choices"))
    )
      continue;
    if (["user", "order", "dynamic"].includes(f.source.kind))
      sources.push({
        fieldId: f.id,
        kind: f.source.kind as "user" | "order" | "dynamic",
        key: f.source.key || f.key,
        ...(f.source.kind === "dynamic" && f.source.dynamicSourceId
          ? { dynamicSourceId: f.source.dynamicSourceId }
          : {}),
        ...(f.source.kind === "dynamic" && f.source.dynamicMode === "choices"
          ? { dynamicMode: "choices" as const }
          : {}),
      });
    else if (f.source.kind === "form")
      sources.push({
        fieldId: f.id,
        kind: "form",
        key: f.source.structId || "",
        instanceId: f.source.instanceId || "",
      });
  }
  if (
    sources.length > 1000 ||
    sources.some((s) => s.key.length > 200 || (s.instanceId?.length || 0) > 200)
  )
    throw new ContextError("CONTEXT_REQUEST_LIMIT");
  const parameters = resolveSourceParameters(
    project,
    options.sourceParameters,
    partId,
  );
  const activeSources = sources.filter(
    (source) =>
      !(
        source.kind === "dynamic" &&
        source.dynamicSourceId &&
        parameters?.[source.dynamicSourceId] === null
      ),
  );
  return {
    version: 1,
    tenantId: actor.tenantId,
    subjectId: actor.subject,
    projectId: project.id,
    projectRevision: project.revision,
    ...(partId ? { partId } : {}),
    sources: activeSources,
    clock: fields.some((f) => f.autoGenerated),
    ...(parameters ? { parameters } : {}),
    ...(options.forceRefresh &&
    parameters &&
    Object.values(parameters).some((value) => value !== null)
      ? { forceRefresh: true }
      : {}),
  };
}
export function projectContext(request: ContextRequest, raw: unknown): Context {
  const context = parseContext(raw),
    maps: Record<string, Map<string, Value>> = {},
    forms = new Map<string, Map<string, Value>>();
  for (const source of request.sources) {
    if (
      source.dynamicSourceId &&
      request.parameters?.[source.dynamicSourceId] === null
    )
      continue;
    const map =
      source.kind === "form"
        ? own(context.forms, source.instanceId || "")
        : own(context, source.kind);
    const value = own(map, source.key);
    if (value === undefined) continue;
    if (source.kind === "form") {
      const values = forms.get(source.instanceId || "") || new Map();
      values.set(source.key, value as Value);
      forms.set(source.instanceId || "", values);
    } else {
      (maps[source.kind] ||= new Map()).set(source.key, value as Value);
    }
  }
  return {
    ...(request.parameters
      ? { sourceParameters: structuredClone(request.parameters) }
      : {}),
    ...Object.fromEntries(
      Object.entries(maps).map(([key, values]) => [
        key,
        Object.fromEntries(values),
      ]),
    ),
    ...(forms.size
      ? {
          forms: Object.fromEntries(
            [...forms].map(([id, values]) => [id, Object.fromEntries(values)]),
          ),
        }
      : {}),
    ...(request.clock && context.now !== undefined ? { now: context.now } : {}),
  };
}
export function contextHash(context: Context): string {
  const canonical = (value: unknown): unknown =>
    Array.isArray(value)
      ? value.map(canonical)
      : value && typeof value === "object"
        ? Object.fromEntries(
            Object.entries(value)
              .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
              .map(([key, v]) => [key, canonical(v)]),
          )
        : value;
  return bytesHash(
    new TextEncoder().encode(JSON.stringify(canonical(parseContext(context)))),
  );
}
export async function resolveHostContext(
  project: Project,
  actor: Principal,
  partId: string | undefined,
  provider?: HostContextProvider,
  signal?: AbortSignal,
  options: { sourceParameters?: unknown; forceRefresh?: boolean } = {},
) {
  const request = contextRequest(project, actor, partId, options);
  if (signal?.aborted) throw new ContextError("CONTEXT_CANCELLED", 499);
  if (!request.sources.length && !request.clock && !request.parameters)
    return { request, context: undefined, hash: undefined };
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let abort = () => {};
  try {
    const raw = await Promise.race([
      Promise.resolve().then(() =>
        provider && (request.sources.length || request.clock)
          ? provider(structuredClone(request), controller.signal)
          : {},
      ),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new ContextError("CONTEXT_PROVIDER_TIMEOUT", 503));
        }, 5000);
        abort = () => {
          controller.abort();
          reject(new ContextError("CONTEXT_CANCELLED", 499));
        };
        signal?.addEventListener("abort", abort, { once: true });
        if (signal?.aborted) abort();
      }),
    ]);
    const context = projectContext(request, raw);
    effectiveFields(project, partId, context);
    return { request, context, hash: contextHash(context) };
  } catch (error) {
    if (error instanceof ContextError) throw error;
    throw new ContextError("CONTEXT_PROVIDER_UNAVAILABLE", 503);
  } finally {
    if (timer) clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}
